import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, Button, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Avatar, Chip, Switch, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, InputAdornment, Tooltip, Typography
} from '@mui/material';
import AddIcon    from '@mui/icons-material/AddRounded';
import EditIcon   from '@mui/icons-material/EditRounded';
import SearchIcon from '@mui/icons-material/SearchRounded';
import { fetchUsers, createUser, updateUser, toggleStatus } from '../../app/slices/usersSlice';
import { fetchDepartments } from '../../app/slices/departmentsSlice';
import { PageHeader }    from '../../components/common/PageHeader';
import { LoadingSpinner }from '../../components/common/LoadingSpinner';
import { PasswordField } from '../../components/common/PasswordField';
import { getInitials }   from '../../utils/helpers';
import { ROLE_OPTIONS }  from '../../utils/constants';

const EMPTY = { name: '', email: '', loginId: '', password: '', role: 'User', departmentId: '', phone: '' };

const isPasswordValid = pw =>
  pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);

export default function UsersPage() {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.users);
  const { list: depts }   = useSelector(s => s.departments);
  const [search, setSearch]     = useState('');
  const [dlg, setDlg]           = useState(false);
  const [editItem, setEditItem]  = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { dispatch(fetchUsers()); dispatch(fetchDepartments()); }, [dispatch]);

  const filtered = list.filter(u =>
    !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setForm(EMPTY); setEditItem(null); setError(''); setDlg(true); };
  const openEdit   = u  => {
    setForm({ name: u.name, email: u.email, loginId: u.loginId, password: '', role: u.role, departmentId: u.departmentId, phone: u.phone || '' });
    setEditItem(u); setError(''); setDlg(true);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    const dto = editItem
      ? { name: form.name, email: form.email, role: form.role, departmentId: Number(form.departmentId), phone: form.phone }
      : { ...form, departmentId: Number(form.departmentId) };
    const res = editItem
      ? await dispatch(updateUser({ id: editItem.id, dto }))
      : await dispatch(createUser(dto));
    if (res.type.endsWith('/rejected')) setError(res.payload || 'Error saving.');
    else setDlg(false);
    setSaving(false);
  };

  const canSave = form.name && form.email && form.departmentId &&
    (editItem || (form.loginId && isPasswordValid(form.password)));

  const roleColor = r => r === 'Admin' ? 'error' : r === 'Manager' ? 'warning' : 'default';

  if (loading && !list.length) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader title="Users" subtitle={`${list.length} total users`}
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add User</Button>} />

      <Card sx={{ p: 2, mb: 2 }}>
        <TextField placeholder="Search users…" value={search} onChange={e => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ minWidth: 260 }} />
      </Card>

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Login ID</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#e0e7ff', color: '#4f46e5', fontSize: '0.7rem', fontWeight: 700 }}>
                      {getInitials(u.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{u.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{u.loginId}</Typography></TableCell>
                <TableCell><Chip label={u.role} size="small" color={roleColor(u.role)} /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{u.departmentName}</Typography></TableCell>
                <TableCell><Switch checked={u.isActive} onChange={() => dispatch(toggleStatus(u.id))} size="small" /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700 }}>
          {editItem ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Full Name" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))} fullWidth required />
          <TextField label="Email" type="email" value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))} fullWidth required />
          {!editItem && (
            <TextField
              label="Login ID" value={form.loginId}
              onChange={e => setForm(p => ({ ...p, loginId: e.target.value }))}
              fullWidth required
              helperText="3–50 characters. Letters, digits, _ . - only."
            />
          )}
          {!editItem && (
            <PasswordField
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
            />
          )}
          <TextField select label="Role" value={form.role}
            onChange={e => setForm(p => ({ ...p, role: e.target.value }))} fullWidth>
            {ROLE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField select label="Department" value={form.departmentId}
            onChange={e => setForm(p => ({ ...p, departmentId: e.target.value }))} fullWidth required>
            {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
          <TextField label="Phone (optional)" value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} fullWidth
            helperText="e.g. +91 99999 99999" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Saving…' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
